import React, { useState, useEffect } from 'react';
import { IconContext } from "react-icons";
import { VscFoldUp, VscFoldDown } from "react-icons/vsc";
import { FaComments } from "react-icons/fa";
import { FiLayout } from "react-icons/fi";
import { Link } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, get } from "firebase/database";
import "./History.css";
import { HistoryProps, HistoryState, HistoryItem } from "../types";

const History: React.FC<HistoryProps> = ({ darkMode, dashboardOnly }) => {
    const [history, setHistory] = useState<{ [key: string]: HistoryItem }>({});
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [historyExtend, setHistoryExtend] = useState<boolean>(false);
    const [sessionList, setSessionList] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [chatOpen, setChatOpen] = useState<boolean>(false);

    useEffect(() => {
        const auth = getAuth();
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsAuthenticated(true);
                setUserId(user.uid);
            } else {
                setIsAuthenticated(false);
                setUserId(null);
            }
        });
    }, []);

    useEffect(() => {
        const fetchHistory = async (): Promise<void> => {
            setLoading(true);
            const db = getDatabase();
            const newHistory: { [key: string]: HistoryItem } = {};

            try {
                // Get user's accessed sandboxes from localStorage
                const localStorageHistory = localStorage.getItem("history");
                const accessedSessions: { [key: string]: string } = localStorageHistory ? JSON.parse(localStorageHistory) : {};

                if (isAuthenticated && userId) {
                    // For authenticated users, fetch their private sandboxes
                    const privateSnapshot = await get(ref(db, `private-sandboxes/${userId}`));
                    if (privateSnapshot.exists()) {
                        const privateData = privateSnapshot.val();
                        Object.keys(privateData).forEach(sessionId => {
                            newHistory[sessionId] = {
                                sessionId,
                                ...privateData[sessionId],
                                isPrivate: true
                            };
                        });
                    }

                    // For authenticated users, also fetch public sandboxes they've accessed
                    // Only fetch sandboxes that exist in their localStorage history
                    const accessedSessionIds = Object.keys(accessedSessions);
                    if (accessedSessionIds.length > 0) {
                        // Fetch each accessed session individually to avoid loading all public sandboxes
                        for (const sessionId of accessedSessionIds) {
                            try {
                                const sessionSnapshot = await get(ref(db, `code-sessions/${sessionId}`));
                                if (sessionSnapshot.exists()) {
                                    const sessionData = sessionSnapshot.val();
                                    newHistory[sessionId] = {
                                        sessionId,
                                        ...sessionData,
                                        isPrivate: false,
                                        lastAccessed: accessedSessions[sessionId] // Use the timestamp from localStorage
                                    };
                                }
                            } catch (error) {
                                console.error(`Error fetching session ${sessionId}:`, error);
                                // If we can't fetch from DB, still show it with basic info from localStorage
                                newHistory[sessionId] = {
                                    sessionId,
                                    lastAccessed: accessedSessions[sessionId],
                                    isPrivate: false,
                                    language: 'Unknown',
                                    status: 'No execution'
                                };
                            }
                        }
                    }
                } else {
                    // For non-authenticated users, only show sandboxes they've accessed
                    const accessedSessionIds = Object.keys(accessedSessions);
                    if (accessedSessionIds.length > 0) {
                        // Fetch each accessed session individually
                        for (const sessionId of accessedSessionIds) {
                            try {
                                const sessionSnapshot = await get(ref(db, `code-sessions/${sessionId}`));
                                if (sessionSnapshot.exists()) {
                                    const sessionData = sessionSnapshot.val();
                                    newHistory[sessionId] = {
                                        sessionId,
                                        ...sessionData,
                                        isPrivate: false,
                                        lastAccessed: accessedSessions[sessionId] // Use the timestamp from localStorage
                                    };
                                }
                            } catch (error) {
                                console.error(`Error fetching session ${sessionId}:`, error);
                                // If we can't fetch from DB, still show it with basic info from localStorage
                                newHistory[sessionId] = {
                                    sessionId,
                                    lastAccessed: accessedSessions[sessionId],
                                    isPrivate: false,
                                    language: 'Unknown',
                                    status: 'No execution'
                                };
                            }
                        }
                    }
                }

                setHistory(newHistory);

                // Convert history object to sessionList array and sort by timestamp
                const sessionsArray = Object.values(newHistory).sort((a, b) => {
                    const aTime = a.lastAccessed ? new Date(a.lastAccessed) : new Date(a.createdon || 0);
                    const bTime = b.lastAccessed ? new Date(b.lastAccessed) : new Date(b.createdon || 0);
                    return bTime.getTime() - aTime.getTime();
                });

                setSessionList(sessionsArray);
            } catch (error) {
                console.error('Error fetching history:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [isAuthenticated, userId]);

    // Add scroll event handling
    useEffect(() => {
        const handleScroll = (e: WheelEvent): void => {
            if (historyExtend) {
                const historyMenu = document.querySelector('.historyMenu') as HTMLElement;
                if (historyMenu) {
                    e.preventDefault();
                    historyMenu.scrollTop += e.deltaY;
                }
            }
        };

        if (historyExtend) {
            document.addEventListener('wheel', handleScroll, { passive: false });
        }

        return () => {
            document.removeEventListener('wheel', handleScroll);
        };
    }, [historyExtend]);

    const toggleHistory = (): void => {
        setHistoryExtend(!historyExtend);
    };

    const toggleChat = (): void => {
        console.log('Chat icon clicked!');
        setChatOpen(!chatOpen);

        // Open Crisp chat
        const openCrispChat = (): void => {
            console.log('Attempting to open Crisp chat...');

            if (typeof window !== 'undefined' && (window as any).$crisp) {
                console.log('Crisp API available, calling methods...');
                try {
                    // Show the chat widget first
                    (window as any).$crisp.push(['do', 'chat:show']);

                    // Then open the chat
                    setTimeout(() => {
                        (window as any).$crisp.push(['do', 'chat:open']);
                        console.log('Crisp chat open command sent');
                    }, 100);

                } catch (error) {
                    console.error('Error with Crisp API:', error);
                }
            }

            // Alternative approach: Find and click the hidden launcher
            setTimeout(() => {
                const launcher = document.querySelector('.crisp-client-launcher') as HTMLElement;
                if (launcher) {
                    console.log('Found launcher, clicking it...');
                    // Temporarily make it visible and clickable
                    launcher.style.display = 'block';
                    launcher.style.visibility = 'visible';
                    launcher.style.opacity = '1';
                    launcher.style.pointerEvents = 'auto';

                    // Click it
                    launcher.click();

                    // Hide it again after clicking
                    setTimeout(() => {
                        launcher.style.display = 'none';
                        launcher.style.visibility = 'hidden';
                        launcher.style.opacity = '0';
                        launcher.style.pointerEvents = 'none';
                    }, 500);
                } else {
                    console.log('No launcher found, trying direct widget approach...');

                    // Try to find the chat widget and trigger it directly
                    const chatWidget = document.querySelector('.crisp-client') as HTMLElement;
                    if (chatWidget) {
                        // Look for any clickable element inside the widget
                        const clickableElement = chatWidget.querySelector('[data-crisp-id], .crisp-chatbox-container, .crisp-chatbox') as HTMLElement;
                        if (clickableElement) {
                            console.log('Found clickable element in widget, clicking...');
                            clickableElement.click();
                        } else {
                            // Fallback: click the widget itself
                            console.log('Clicking chat widget directly...');
                            chatWidget.click();
                        }
                    }
                }
            }, 200);
        };

        openCrispChat();

    };

    const renderHistoryIcon = (): React.ReactElement => {
        const icon = historyExtend ? <VscFoldDown /> : <VscFoldUp />;
        return (
            <div className="historyIcon" onClick={toggleHistory}>
                <IconContext.Provider value={{ size: "0.75em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                    {icon}
                </IconContext.Provider>
            </div>
        );
    };

    const renderChatIcon = (): React.ReactElement => {
        return (
            <div className="chatIcon" onClick={toggleChat} title="Open chat">
                <IconContext.Provider value={{ size: "0.75em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                    <FaComments />
                </IconContext.Provider>
            </div>
        );
    };


    const renderHistoryItem = (item: HistoryItem): React.ReactElement => {
        const { sessionId, createdon, language, status, lastAccessed, isPrivate } = item;
        // Ensure timestamps are strings
        const displayTimestamp =
            typeof lastAccessed === 'string'
                ? lastAccessed
                : typeof createdon === 'string'
                    ? createdon
                    : '';
        return (
            <div key={sessionId} className="history-item">
                <Link to={`/box/${sessionId}`} className="history-link">
                    <div className="history-header">
                        <span className="history-url">
                            {isPrivate ? '🔒 ' : ''}interviewsandbox.com/box/{sessionId}
                        </span>
                        <span className="history-timestamp">{displayTimestamp}</span>
                    </div>
                    <div className="history-details">
                        <div className="history-language">
                            <span className="history-label">Language:</span>
                            <span className="history-value">{language || 'Unknown'}</span>
                        </div>
                        <div className="history-status">
                            <span className="history-label">Status:</span>
                            <span className={`history-value status-${status ? status.toLowerCase() : 'no-execution'}`}>{status || 'No execution'}</span>
                        </div>
                    </div>
                </Link>
            </div>
        );
    };

    return (
        <div className={`history ${darkMode ? 'history-dark' : ''}`}>
            <div className="bottom-left-icons">
                <Link to="/dashboard" className="dashboardIcon" title="Dashboard" aria-label="Go to dashboard">
                    <IconContext.Provider value={{ size: "0.75em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                        <FiLayout />
                    </IconContext.Provider>
                </Link>
                {!dashboardOnly && (
                    <>
                        {renderHistoryIcon()}
                        {renderChatIcon()}
                    </>
                )}
            </div>
            {!dashboardOnly && (
            <div className={historyExtend ? "historyMenu" : "historyMenuClose"}>
                {loading && <div className="history-loading">Loading...</div>}
                {historyExtend && !loading && sessionList.length === 0 && (
                    <div className="history-empty">No sandboxes found.</div>
                )}
                {historyExtend && !loading && sessionList.map(renderHistoryItem)}
            </div>
            )}
        </div>
    );
};

export default History;
